import React from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '../App';
import { Shield, Mail, Phone, Lock } from 'lucide-react';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/30 rounded-full mb-6">
            <Shield className="w-4 h-4 text-primary" />
            <span className="text-primary text-sm font-bold uppercase tracking-wider">Privacy Policy</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-heading font-bold mb-4">
            Privacy Policy
          </h1>
          <p className="text-muted-foreground">
            Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>

        <div className="prose prose-invert max-w-none space-y-8">
          <section className="bg-card border border-border rounded-xl p-8">
            <h2 className="text-2xl font-heading font-bold mb-4">Introduction</h2>
            <p className="text-muted-foreground leading-relaxed">
              Luck or Skills ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
            </p>
          </section>

          <section className="bg-card border border-border rounded-xl p-8">
            <h2 className="text-2xl font-heading font-bold mb-4">Information We Collect</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We collect information that you provide directly to us, including:
            </p>
            <ul className="space-y-2 text-muted-foreground">
              <li>• Name and contact information (email address, phone number)</li>
              <li>• Account credentials and profile information</li>
              <li>• Payment and billing information</li>
              <li>• Communications with us</li>
              <li>• Any other information you choose to provide</li>
            </ul>
          </section>

          <section className="bg-card border-2 border-primary/30 rounded-xl p-8">
            <div className="flex items-center gap-3 mb-4">
              <Phone className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-heading font-bold">SMS / Text Messaging Policy</h2>
            </div>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                <strong className="text-foreground">Purpose:</strong> Phone numbers are collected for appointment confirmations, appointment reminders, service updates, and follow-up communications related to your poker training and membership with Luck or Skills.
              </p>
              <p>
                <strong className="text-foreground">Consent:</strong> By providing your phone number and checking the SMS consent box, you agree to receive text messages from Luck or Skills. SMS consent is optional and not a condition of purchase or use of our services.
              </p>
              <p>
                <strong className="text-foreground">Message Frequency:</strong> Message frequency may vary depending on your appointment schedule and service updates. We typically send appointment reminders 24 hours before scheduled sessions.
              </p>
              <p>
                <strong className="text-foreground">Opt-Out:</strong> You can opt out of SMS messages at any time by replying STOP to any text message you receive from us. After you opt out, you will receive one final confirmation message.
              </p>
              <p>
                <strong className="text-foreground">Help:</strong> For assistance, reply HELP to any message or contact us at info@luckorskills.com.
              </p>
              <p>
                <strong className="text-foreground">Rates:</strong> Message and data rates may apply based on your mobile carrier's plan. Please check with your carrier for details.
              </p>
              <p>
                <strong className="text-foreground">Data Sharing:</strong> Your phone number and SMS consent information are never sold or shared with third parties for marketing purposes. We may share your phone number only with service providers necessary to deliver messaging services, such as SMS platforms (e.g., GoHighLevel) or mobile carriers.
              </p>
              <p>
                <strong className="text-foreground">Data Security:</strong> We implement appropriate security measures to protect your phone number and personal information from unauthorized access, alteration, or disclosure.
              </p>
            </div>
          </section>

          <section className="bg-card border border-border rounded-xl p-8">
            <h2 className="text-2xl font-heading font-bold mb-4">How We Use Your Information</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We use the information we collect to:
            </p>
            <ul className="space-y-2 text-muted-foreground">
              <li>• Provide, maintain, and improve our services</li>
              <li>• Process transactions and send related information</li>
              <li>• Send appointment confirmations and reminders</li>
              <li>• Respond to your comments and questions</li>
              <li>• Provide customer service and support</li>
              <li>• Send you technical notices and updates</li>
              <li>• Communicate with you about products, services, and events</li>
            </ul>
          </section>

          <section className="bg-card border border-border rounded-xl p-8">
            <h2 className="text-2xl font-heading font-bold mb-4">Information Sharing and Disclosure</h2>
            <p className="text-muted-foreground leading-relaxed">
              We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
            </p>
            <ul className="space-y-2 text-muted-foreground mt-4">
              <li>• With service providers who perform services on our behalf</li>
              <li>• To comply with legal obligations</li>
              <li>• To protect our rights and safety</li>
              <li>• With your consent</li>
            </ul>
          </section>

          <section className="bg-card border border-border rounded-xl p-8">
            <h2 className="text-2xl font-heading font-bold mb-4">Data Security</h2>
            <p className="text-muted-foreground leading-relaxed">
              We implement reasonable security measures to protect your information from unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure.
            </p>
          </section>

          <section className="bg-card border border-border rounded-xl p-8">
            <h2 className="text-2xl font-heading font-bold mb-4">Your Rights</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              You have the right to:
            </p>
            <ul className="space-y-2 text-muted-foreground">
              <li>• Access your personal information</li>
              <li>• Correct inaccurate information</li>
              <li>• Request deletion of your information</li>
              <li>• Opt out of marketing communications</li>
              <li>• Opt out of SMS messages by replying STOP</li>
            </ul>
          </section>

          <section className="bg-card border border-border rounded-xl p-8">
            <h2 className="text-2xl font-heading font-bold mb-4">Contact Us</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              If you have questions about this Privacy Policy or our privacy practices, please contact us:
            </p>
            <div className="space-y-2 text-muted-foreground">
              <p><strong className="text-foreground">Luck or Skills</strong></p>
              <p>New Braunfels, TX 78130</p>
              <p>
                <Mail className="inline w-4 h-4 mr-2" />
                <a href="mailto:info@luckorskills.com" className="text-primary hover:text-primary/80">info@luckorskills.com</a>
              </p>
              <p>
                <Phone className="inline w-4 h-4 mr-2" />
                <a href="tel:+18302372821" className="text-primary hover:text-primary/80">(830) 237-2821</a>
              </p>
            </div>
          </section>

          <section className="bg-card border border-border rounded-xl p-8">
            <h2 className="text-2xl font-heading font-bold mb-4">Changes to This Policy</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
            </p>
          </section>
        </div>

        <div className="mt-12 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-semibold"
          >
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
